import Head from 'next/head'
import Image from 'next/image'
import { useViewport } from 'contexts/viewport'
import { useAppContext } from 'contexts/app_context'
import { TeamGamesMetaDescription, TeamGamesOGImagePath } from 'utils/constants'

import AppHeader from 'components/app_header'
import ModalJoinOuting from 'components/modals/modal_join_outing'
import AppFooter from 'components/app_footer'

import styles from 'styles/about.module.scss'

const About = () => {
  const { isMobile } = useViewport()
  const [state, dispatch] = useAppContext()

  const showModalJoinOuting = () => {
    dispatch({
      type: 'SHOW_MODAL_JOIN_OUTING',
      payload: true,
    })
  }

  return (
    <div className={styles.container} id="about_page">
      <Head>
        <title>About Team Games</title>
        <link rel="icon" href="/images/modal_logo.png" />
        <meta name="description" content={TeamGamesMetaDescription} />
        <meta name="og:image" content={TeamGamesOGImagePath} />
      </Head>
      <div className={styles.header_section}>
        <AppHeader transparent />
        <div className={styles.header_bg}>
          {isMobile ? (
            <div className={styles.header_bg_img}>
              <Image src="/images/about/background_mobile.png" alt="Game for Every Team" layout="fill" />
            </div>
          ) : (
            <div className={styles.header_bg_img}>
              <Image src="/images/about/background.png" alt="Game for Every Team" layout="fill" />
            </div>
          )}
        </div>
        <div className={styles.header_container}>
          <div className={styles.header_left}>
            <div className={'hero ' + styles.header_title}>Teams that Play Together Stay Together</div>
            <div className={'description_small ' + styles.header_desc}>
              Professor J. Richard Hackman, an expert in organizational behavior, found that teamwork is most effective
              when it exists under certain “enabling conditions.” Those include: a compelling direction (goal), a strong
              structure, and a supportive context. Here at Team Games, we provide these conditions through entertaining
              games that you can play together anytime, anywhere. Team building exercises have never been this easy and
              fun!
            </div>
          </div>
          <div className={styles.header_right}>
            <div className={styles.header_right_image_container}>
              <Image src="/images/about/header.png" alt="Header" layout="fill" />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.metrics_section}>
        <div className={styles.metrics_section_bg_vector}>
          <Image src="/images/about/metrics_bg_vector.png" alt="Metrics Background" layout="fill" />
        </div>
        <div className={styles.metrics_section_cards}>
          <div className={styles.metrics_section_card}>
            <div className={styles.metrics_section_card_bg_container}>
              <Image src="/images/about/metrics_left.png" alt="Card Image" width={135} height={152} />
            </div>
            <div className={styles.metrics_section_card_number + ' heading_1'}>10000+</div>
            <div className={styles.metrics_section_card_content + ' description_large'}>Teams brought together</div>
          </div>
          <div className={styles.metrics_section_card}>
            <div className={styles.metrics_section_card_bg_container}>
              <Image src="/images/about/metrics_center.png" alt="Card Image" width={169} height={117} />
            </div>
            <div className={styles.metrics_section_card_number + ' heading_1'}>10000+</div>
            <div className={styles.metrics_section_card_content + ' description_large'}>
              Hours spent in virtual team Outings
            </div>
          </div>
          <div className={styles.metrics_section_card}>
            <div className={styles.metrics_section_card_bg_container}>
              <Image src="/images/about/metrics_right.png" alt="Card Image" width={165} height={108} />
            </div>
            <div className={styles.metrics_section_card_number + ' heading_1'}>8+</div>
            <div className={styles.metrics_section_card_content + ' description_large'}>Games available now</div>
          </div>
        </div>
      </div>

      <div className={styles.mission_section_text_left}>
        <div className={styles.mission_section_left}>
          <div className={styles.mission_section_title + ' heading_1'}>Our Mission</div>
          <div className={styles.mission_section_content + ' description_medium'}>
            When you schedule an Outing on Team Games, we pledge to make your team building experience easy, simple, and
            of course - fun - whether your company is divided by cubicles, oceans, or anything in between. Plus, things
            will never be the same once you discover Susan from accounting has got game. Here’s wishing you all the
            happiest surprises and friendly competition - from our team, to yours.
          </div>
        </div>
        <div className={styles.mission_section_right}>
          <div className={styles.mission_section_right_image_container}>
            <Image src="/images/about/misson.png" alt="Image" layout="fill" />
          </div>
        </div>
      </div>

      <div className={styles.mission_section_text_right}>
        <div className={styles.mission_section_left}>
          <div className={styles.mission_section_left_image_container}>
            <Image src="/images/about/boom_to_teamgames.png" alt="Image" layout="fill" />
          </div>
        </div>
        <div className={styles.mission_section_right}>
          <div className={styles.mission_section_title + ' heading_1'}>From BoomTV to Team Games</div>
          <div className={styles.mission_section_content + ' description_medium'}>
            BoomTV is a leading esports entertainment organizer with over 33,000 tournaments under our belt, and boy, do
            we have fun. But you don’t have to be a professional gamer to reap the benefits that video games offer. We
            believe every company deserves easy access to fun team building activities, so Team Games was born.
            <br />
            There are over 2.7B people who enjoy video games, and most of them turn to quick, yet challenging game
            sessions on mobile. That’s because these games offer high rewards for low time investment. With Team Games,
            you can offer those same rewards with your organization.
          </div>
        </div>
      </div>

      <div className={styles.mission_section_text_left}>
        <div className={styles.mission_section_left}>
          <div className={styles.mission_section_title + ' heading_1'}>What Makes Us Different</div>
          <div className={styles.mission_section_content + ' description_medium'}>
            The best solutions are often the simplest. That’s why we’ve designed team Outings to be scheduled in as
            little as 30 seconds. And, no software to download means you can jump in and out of the action on your
            schedule, not ours. Our games range from serious competition to silly fun, with new titles added all the
            time to keep things fresh. We hand-select each game to meet our standards of challenge and fun so that your
            Outings are the best they can be, every time. You can rely on Team Games to be there for you whenever you
            need us.
          </div>
        </div>
        <div className={styles.mission_section_right}>
          <div className={styles.mission_section_right_image_container_2}>
            <Image src="/images/about/difference.png" alt="Image" layout="fill" />
          </div>
        </div>
      </div>

      <div className={styles.mission_section_text_right_blue}>
        <div className={styles.mission_section_text_right_blue_container}>
          <div className={styles.mission_section_left}>
            <div className={styles.mission_section_left_image_container}>
              <Image src="/images/about/love_your_team.png" alt="Image" layout="fill" />
            </div>
          </div>
          <div className={styles.mission_section_right}>
            <div className={styles.mission_section_title + ' heading_1'}>
              We
              <svg width="28" height="26" viewBox="0 0 28 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M27.1267 3.66903C25.9541 1.19196 22.5761 -0.834729 18.6467 0.345256C16.7691 0.903487 15.1309 2.10063 13.9998 3.74109C12.8687 2.10063 11.2305 0.903487 9.35287 0.345256C5.41479 -0.816714 2.04555 1.19196 0.872876 3.66903C-0.772366 7.13693 -0.0897655 11.0372 2.90317 15.2617C5.24852 18.5675 8.60026 21.9183 13.466 25.8095C13.6197 25.933 13.8092 26 14.0042 26C14.1992 26 14.3886 25.933 14.5424 25.8095C19.3994 21.9273 22.7598 18.6035 25.1052 15.2617C28.0894 11.0372 28.772 7.13693 27.1267 3.66903Z"
                  fill="white"
                />
              </svg>
              Your Team
            </div>
            <div className={styles.mission_section_content + ' description_medium'}>
              If there’s one thing we’ve learned in all our years of organizing major esports tournaments it’s that
              skills don’t amount to much if you don’t have a strong team. You inspire us everyday with your creative
              schedules, on-the-fly problem solving, and the way you still manage to have fun. Team Games is here to
              support you whether you need to unwind or step up your game. Schedule an Outing today and let us do the
              heavy lifting. You’ve earned it.
            </div>
          </div>
        </div>
      </div>

      <div className={styles.values_section}>
        <div className={styles.values_section_title + ' heading_1'}>What we’re about</div>
        <div className={styles.values_section_cards}>
          <div className={styles.values_section_card}>
            <div className={styles.values_section_card_bg_container}>
              <Image src="/images/about/values_left.png" alt="Card Image" width={185} height={117} />
            </div>
            <div className={styles.values_section_card_title + ' heading_2'}>Simplicity</div>
            <div className={styles.values_section_card_desc + ' description_small'}>
              With Team Games, what you see is what you get. No extra software, no complicated systems. Just your team
              having a blast together.
            </div>
          </div>
          <div className={styles.values_section_card}>
            <div className={styles.values_section_card_bg_container}>
              <Image src="/images/about/values_center.png" alt="Card Image" width={192} height={128} />
            </div>
            <div className={styles.values_section_card_title + ' heading_2'}>Adventure</div>
            <div className={styles.values_section_card_desc + ' description_small'}>
              From Poker to Drawn Together, our growing list of games lets your team challenge themselves and try
              something new.
            </div>
          </div>
          <div className={styles.values_section_card}>
            <div className={styles.values_section_card_bg_container}>
              <Image src="/images/about/values_right.png" alt="Card Image" width={138} height={138} />
            </div>
            <div className={styles.values_section_card_title + ' heading_2'}>Meaningful Work</div>
            <div className={styles.values_section_card_desc + ' description_small'}>
              Today’s workforce wants to be part of something bigger. Team Games believes that work should bring people
              together, not separate them.
            </div>
          </div>
        </div>
      </div>

      <div className={styles.team_section}>
        <div className={styles.team_section_bg_vector}>
          <Image src="/images/about/team_bg_vector.png" alt="Bg" layout="fill" />
        </div>
        <div className={styles.team_section_container}>
          <div className={styles.team_section_title + ' heading_1'}>Meet the Team</div>
          <div className={styles.team_section_desc + ' description_large'}>
            Team Games is made up of diverse professionals from gaming, tech, sports, and more.
          </div>
          <div className={styles.team_section_cards}>
            <a href="https://www.linkedin.com/in/guptasumit/" target="_blank" rel="noreferrer">
              <div className={styles.team_section_card}>
                <div className={styles.team_section_card_bg}>
                  <Image src="/images/about/profile/sumit.png" alt="Profile Image" layout="fill" />
                </div>
                <div className={styles.team_section_card_name + ' heading_3'}>Sumit Gupta</div>
                <div className={styles.team_section_card_position + ' heading_4'}>CEO</div>
              </div>
            </a>
            <a>
              <div className={styles.team_section_card}>
                <div className={styles.team_section_card_bg} />
                <div className={styles.team_section_card_name + ' heading_3'}>Ha Nguyen</div>
                <div className={styles.team_section_card_position + ' heading_4'}>CTO</div>
              </div>
            </a>
            <a href="https://www.linkedin.com/in/garyfung/" target="_blank" rel="noreferrer">
              <div className={styles.team_section_card}>
                <div className={styles.team_section_card_bg}>
                  <Image src="/images/about/profile/gary_fung.jpg" alt="Profile Image" layout="fill" />
                </div>
                <div className={styles.team_section_card_name + ' heading_3'}>Gary Fung</div>
                <div className={styles.team_section_card_position + ' heading_4'}>Engineering</div>
              </div>
            </a>
            <a href="https://www.linkedin.com/in/christian-gross-esports/" target="_blank" rel="noreferrer">
              <div className={styles.team_section_card}>
                <div className={styles.team_section_card_bg}>
                  <Image src="/images/about/profile/christian.png" alt="Profile Image" layout="fill" />
                </div>
                <div className={styles.team_section_card_name + ' heading_3'}>Christian Gross</div>
                <div className={styles.team_section_card_position + ' heading_4'}>Product</div>
              </div>
            </a>
            <a href="https://twitter.com/DrulkTV" target="_blank" rel="noreferrer">
              <div className={styles.team_section_card}>
                <div className={styles.team_section_card_bg}>
                  <Image src="/images/about/profile/dakota.png" alt="Profile Image" layout="fill" />
                </div>
                <div className={styles.team_section_card_name + ' heading_3'}>Dakota Harding</div>
                <div className={styles.team_section_card_position + ' heading_4'}>Marketing</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className={styles.section_cta}>
        <div className={styles.section_cta_container}>
          <div className={styles.section_cta_title + ' hero'}>Get your team involved through Team Games</div>
          <div className={styles.section_cta_desc + ' description_large'}>
            Discover the new way to build camaraderie and teamwork. Only at Team Games.
          </div>
          <div className={styles.section_cta_buttons}>
            <div className={styles.section_cta_button} onClick={showModalJoinOuting}>
              <span>Create Now</span>
              <div className={styles.arrow} />
            </div>
            <div
              className={styles.section_cta_button + ' ' + styles.section_cta_button_no_bg}
              onClick={() => (location.href = '/contact-us')}
            >
              <span>Contact Us</span>
            </div>
          </div>
        </div>
      </div>
      <ModalJoinOuting />
      <AppFooter />
    </div>
  )
}

export default About
